import api from '@/shared/utils/apiConfig';
import type { ImportCsvPreviewResult, ImportInspectResult } from '@/shared/import-export';
import {
  downloadBlob,
  filenameFromContentDisposition,
} from '@/shared/import-export';
import type { StaffMember } from '../utils/staffForm';

export type { StaffMember };

/** Active staff used by pickers. Same shape as `GET /staff?view=roster`. */
export interface StaffRosterRef {
  id: string;
  firstName?: string;
  lastName?: string;
  designation?: string;
  avatarUrl?: string | null;
}

export interface StaffRosterDepartment {
  id: string;
  code: string;
  name: string;
  leaveApprovers?: StaffRosterRef[];
  leaveFollowers?: StaffRosterRef[];
}

export interface StaffRosterMember {
  _id: string;
  firstName: string;
  lastName: string;
  staffCode?: string;
  isDoctor: boolean;
  isAssistant: boolean;
  designation?: string;
  specialization?: string;
  avatarUrl: string | null;
  status: string;
  department: StaffRosterDepartment | null;
  primaryClinics: string[];
  user: string | null;
  leaveApprovers: StaffRosterRef[];
  leaveFollowers: StaffRosterRef[];
}

export const STAFF_ROSTER_LIMIT = 1000;

export interface GetStaffParams {
  search?: string;
  department?: string;
  employeeType?: string;
  isDoctor?: boolean;
  isAssistant?: boolean;
  status?: string;
  page?: number;
  limit?: number;
  sort?: string;
  clinicScope?: string;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  count?: number;
  total?: number;
  page?: number;
  pages?: number;
  message?: string;
}

export const staffAPI = {
  getAll: async (params: GetStaffParams = {}): Promise<ApiResponse<StaffMember[]>> => {
    const response = await api.get('/staff', { params });
    return response.data;
  },

  /** Active picker list for one clinic scope. `all` ignores the app-bar clinic. */
  getRoster: async (clinicScope: string): Promise<StaffRosterMember[]> => {
    const response = await api.get<ApiResponse<StaffRosterMember[]>>('/staff', {
      params: { status: 'active', limit: STAFF_ROSTER_LIMIT, view: 'roster' },
      headers: { 'X-Clinic-Scope': clinicScope },
    });
    return response.data.data ?? [];
  },

  getOne: async (id: string): Promise<ApiResponse<StaffMember>> => {
    const response = await api.get(`/staff/${id}`);
    return response.data;
  },

  create: async (payload: Record<string, unknown>): Promise<ApiResponse<StaffMember>> => {
    const response = await api.post('/staff', payload);
    return response.data;
  },

  update: async (
    id: string,
    payload: Record<string, unknown>
  ): Promise<ApiResponse<StaffMember>> => {
    const response = await api.put(`/staff/${id}`, payload);
    return response.data;
  },

  remove: async (id: string): Promise<ApiResponse<{ userDeleted?: boolean }>> => {
    const response = await api.delete(`/staff/${id}`);
    return response.data;
  },

  getPractitioners: async (): Promise<ApiResponse<StaffMember[]>> => {
    const response = await api.get('/staff', {
      params: { isDoctor: true, status: 'active' },
    });
    return response.data;
  },

  search: async (query: string): Promise<ApiResponse<StaffMember[]>> => {
    const response = await api.get('/staff', { params: { search: query } });
    return response.data;
  },

  uploadAvatar: async (
    id: string,
    file: File
  ): Promise<{ avatarUrl: string; avatarFileId: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/staff/${id}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data.data;
  },

  deleteAvatar: async (id: string): Promise<void> => {
    await api.delete(`/staff/${id}/avatar`);
  },

  exportCsv: async (): Promise<void> => {
    const response = await api.get('/staff/export', {
      responseType: 'blob',
    });
    const filename = filenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
      'staff.csv'
    );
    downloadBlob(response.data as Blob, filename);
  },

  downloadImportTemplate: async (): Promise<void> => {
    const response = await api.get('/staff/import/template', {
      responseType: 'blob',
    });
    const filename = filenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
      'staff-import-template.csv'
    );
    downloadBlob(response.data as Blob, filename);
  },

  inspectImport: async (
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<ApiResponse<ImportInspectResult>> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/staff/import/inspect', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
        onProgress(percent);
      },
    });
    return response.data;
  },

  importCsv: async (
    file: File,
    dryRun: boolean,
    onProgress?: (percent: number) => void,
    columnMapping?: Record<string, string>
  ): Promise<ApiResponse<ImportCsvPreviewResult>> => {
    const formData = new FormData();
    formData.append('file', file);
    if (columnMapping && Object.keys(columnMapping).length > 0) {
      formData.append('columnMapping', JSON.stringify(columnMapping));
    }
    const response = await api.post('/staff/import', formData, {
      params: { dryRun: dryRun ? 'true' : 'false' },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
        onProgress(percent);
      },
    });
    return response.data;
  },
};
